import { DataProviderPayload } from './types';

type ListCache = {
  data?: unknown;
  total?: number;
  pageInfo?: { hasNextPage?: boolean; hasPreviousPage?: boolean };
  meta?: { countDeferred?: boolean };
};

type PublishDeferredListTotal = (resource: string, payload: DataProviderPayload, total: number) => void;

type InFlightCount = {
  controller: AbortController;
  promise: Promise<void>;
};

const exactTotals = new Map<string, number>();
const inflight = new Map<string, InFlightCount>();

let publish: PublishDeferredListTotal | null = null;

/** Filter and sort identify one total. Pagination does not. */
export const listTotalIdentity = (resource: string, payload: DataProviderPayload) =>
  JSON.stringify([resource, payload.filter ?? {}, payload.sort ?? null]);

/** Same key react-admin uses for one page of the candidate or adverse list. */
export const fcraListQueryKey = (resource: string, payload: DataProviderPayload) => {
  const params: Record<string, unknown> = {
    filter: payload.filter ?? {},
    pagination: payload.pagination,
    sort: payload.sort,
  };
  if (payload.meta) params.meta = payload.meta;
  return [resource, 'getList', params] as const;
};

export const matchesDeferredListQuery = (
  queryKey: readonly unknown[],
  resource: string,
  payload: DataProviderPayload
) => {
  if (queryKey[0] !== resource || queryKey[1] !== 'getList') return false;
  const params = queryKey[2];
  if (!params || typeof params !== 'object') return false;
  const record = params as { filter?: unknown; sort?: unknown };
  return (
    listTotalIdentity(resource, {
      filter: (record.filter ?? {}) as DataProviderPayload['filter'],
      sort: record.sort as DataProviderPayload['sort'],
    }) === listTotalIdentity(resource, payload)
  );
};

export const applyExactTotal = <T extends ListCache>(current: T | undefined, total: number): T | undefined => {
  if (!current?.data) return current;
  const { pageInfo: _pageInfo, ...rest } = current;
  return { ...rest, total, meta: { countDeferred: false } };
};

export const lookupExactTotal = (resource: string, payload: DataProviderPayload) =>
  exactTotals.get(listTotalIdentity(resource, payload));

export const rememberExactTotal = (resource: string, payload: DataProviderPayload, total: number) => {
  exactTotals.set(listTotalIdentity(resource, payload), total);
};

export const registerDeferredListTotal = (next: PublishDeferredListTotal | null) => {
  publish = next;
};

export const publishDeferredListTotal = (resource: string, payload: DataProviderPayload, total: number) => {
  publish?.(resource, payload, total);
};

const isSameResource = (identity: string, resource: string) => identity.startsWith(`["${resource}",`);

/**
 * One count per filter and sort. A newer filter on the same list aborts the
 * previous count. A repeat of the same list joins the count already running.
 */
export const scheduleExactTotal = (
  resource: string,
  payload: DataProviderPayload,
  load: (signal: AbortSignal) => Promise<number>
) => {
  const key = listTotalIdentity(resource, payload);
  for (const [otherKey, entry] of inflight) {
    if (otherKey !== key && isSameResource(otherKey, resource)) {
      entry.controller.abort();
      inflight.delete(otherKey);
    }
  }
  if (inflight.has(key)) return;

  const controller = new AbortController();
  if (payload.signal) {
    if (payload.signal.aborted) controller.abort();
    else payload.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const promise = load(controller.signal)
    .then((total) => {
      rememberExactTotal(resource, payload, total);
      queueMicrotask(() => publishDeferredListTotal(resource, payload, total));
    })
    .catch(() => undefined)
    .finally(() => {
      if (inflight.get(key)?.controller === controller) inflight.delete(key);
    });
  inflight.set(key, { controller, promise });
};

export const clearDeferredListState = () => {
  for (const entry of inflight.values()) entry.controller.abort();
  inflight.clear();
  exactTotals.clear();
  publish = null;
};
